import { WizardData } from '../ProbateWizard';

export type ShareState = {
    data: WizardData;
    selectedCategories: string[];
    currentStep: number;
    sharedAt: string;
};

const SHARE_HASH_PREFIX = 'share=';

// ---------------------------------------------------------------------------
// Encode / decode (base64 with UTF-8 support)
// ---------------------------------------------------------------------------

function encode(obj: unknown): string {
    const json = JSON.stringify(obj);
    const bytes = new TextEncoder().encode(json);
    let binary = '';
    bytes.forEach(b => { binary += String.fromCharCode(b); });
    return btoa(binary);
}

function decode<T>(str: string): T {
    const binary = atob(str);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return JSON.parse(new TextDecoder().decode(bytes)) as T;
}

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

export function buildShareUrl(state: Omit<ShareState, 'sharedAt'>): string {
    const payload: ShareState = { ...state, sharedAt: new Date().toISOString() };
    const encoded = encode(payload);
    const url = new URL(window.location.href);
    url.hash = `${SHARE_HASH_PREFIX}${encoded}`;
    return url.toString();
}

export function readShareStateFromUrl(): ShareState | null {
    try {
        const hash = window.location.hash.slice(1); // strip leading #
        if (!hash.startsWith(SHARE_HASH_PREFIX)) return null;
        const encoded = hash.slice(SHARE_HASH_PREFIX.length);
        if (!encoded) return null;
        const parsed = decode<ShareState>(encoded);
        if (!parsed?.data || !parsed?.sharedAt) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function clearShareFromUrl(): void {
    const url = new URL(window.location.href);
    url.hash = '';
    window.history.replaceState(null, '', url.toString());
}

export async function copyToClipboard(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
}
